import LocalityPanel from "@/components/LocalityPanel";
import ReportCoverage from "@/components/ReportCoverage";
import ResearchOverview from "@/components/ResearchOverview";
import ReportSummaryStrip from "@/components/ReportSummaryStrip";
import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import AddressLookup from "@/components/AddressLookup";
import { useSession } from "@/components/Account";
import { SaveReport } from "@/components/SavedReports";
import { Link } from "react-router-dom";
import MetricCard from "@/components/MetricCard";
import EvidenceReading from "@/components/EvidenceReading";
import PartnerPanel from "@/components/PartnerPanel";
import { reportMetrics } from "@/lib/reportMetrics";
import { findSuburbInsight, r2Listings } from "@/data/suburbInsights";
import ReportAssistant, {
  ReportAssistantPreview,
} from "@/components/ReportAssistant";

const RESEARCH_SESSION_KEY = "propai:research-session";

function readResearchSession() {
  try {
    return JSON.parse(window.sessionStorage.getItem(RESEARCH_SESSION_KEY));
  } catch {
    return null;
  }
}

function matchesResearchLink(session, params) {
  if (!session?.report || !params.has("address")) return false;
  const sameAddress =
    session.inputs?.address?.trim().toLowerCase() ===
    params.get("address")?.trim().toLowerCase();
  const requestedPrice = params.get("price");
  const samePrice =
    !requestedPrice || Number(session.inputs?.price) === Number(requestedPrice);
  return sameAddress && samePrice;
}

const goalPresentation = {
  invest: {
    heading: "Investor view",
    detail:
      "Cash flow, rent evidence and purchase costs appear first. The underlying evidence is unchanged.",
    order: [
      "rent",
      "purchase_costs",
      "flood",
      "planning",
      "transport",
      "neighbourhood",
      "schools",
      "bushfire",
      "asking_price",
    ],
    questions: [
      "Check achievable rent for the exact property and lease conditions.",
      "Compare insurance, rates, strata and maintenance quotes with your assumptions.",
    ],
  },
  live_in: {
    heading: "Home buyer view",
    detail:
      "Location, planning and household checks appear first. The underlying evidence is unchanged.",
    order: [
      "flood",
      "planning",
      "schools",
      "transport",
      "neighbourhood",
      "purchase_costs",
      "rent",
      "bushfire",
      "asking_price",
    ],
    questions: [
      "Check school catchment eligibility and travel times for your household.",
      "Review planning information and nearby development proposals.",
    ],
  },
  own_review: {
    heading: "Owner review",
    detail:
      "Property exposure and ownership-cost checks appear first. The underlying evidence is unchanged.",
    order: [
      "flood",
      "planning",
      "purchase_costs",
      "neighbourhood",
      "transport",
      "schools",
      "rent",
      "bushfire",
      "asking_price",
    ],
    questions: [
      "Confirm current insurance cover and any changes to your property or title.",
      "Review council reports and maintenance needs before making decisions.",
    ],
  },
};

function orderedMetrics(metrics, goal) {
  const order = goalPresentation[goal].order;
  return [...metrics].sort((left, right) => {
    const leftIndex = order.indexOf(left.id);
    const rightIndex = order.indexOf(right.id);
    return (
      (leftIndex === -1 ? order.length : leftIndex) -
      (rightIndex === -1 ? order.length : rightIndex)
    );
  });
}

export default function Research() {
  const {
    draft,
    setDraft,
    ownershipContext,
    setOwnershipContext,
  } = useSession();
  const params = new URLSearchParams(window.location.search);
  const [sessionRestore] = useState(() => readResearchSession());
  const restored =
    !params.has("address") &&
    ownershipContext?.inputs?.address === draft?.address
      ? ownershipContext
      : matchesResearchLink(sessionRestore, params)
        ? sessionRestore
        : null;
  const linkedListing = r2Listings.find(
    (listing) => listing.slug === params.get("listing"),
  );
  const [form, setForm] = useState(
    restored?.inputs || (!params.has("address") && draft) || {
      address: params.get("address") || "",
      price: params.get("price") || "",
      property_type: [
        "house",
        "unit",
        "townhouse",
        "land",
        "house_and_land",
      ].includes(params.get("type"))
        ? params.get("type")
        : "house",
      goal: "invest",
      postcode: "",
      bedrooms: "",
      address_id: null,
      confirmed: false,
    },
  );
  useEffect(() => setDraft(form), [form, setDraft]);
  const [report, setReport] = useState(restored?.report || null);
  const reportHeading = useRef(null);
  useEffect(() => {
    if (report) reportHeading.current?.focus();
  }, [report]);
  const [locality, setLocality] = useState(
    restored?.locality || restored?.report?.locality || null,
  );
  const [selectedLayers, setSelectedLayers] = useState(
    restored?.selectedLayers || [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fallbackGuide = !form.address_id
    ? findSuburbInsight(form.address, form.postcode)
    : null;
  const presentedMetrics = reportMetrics(
    report?.evidence?.metrics,
    locality,
    form.address_id,
    fallbackGuide,
  );
  const sourcedMetrics = presentedMetrics.filter(
    (metric) => metric.status !== "unavailable",
  );
  const unassessedMetrics = presentedMetrics.filter(
    (metric) => metric.status === "unavailable",
  );
  const requestVersion = useRef(0);
  useEffect(
    () => () => {
      requestVersion.current += 1;
    },
    [],
  );
  const invalidate = () => {
    try {
      window.sessionStorage.removeItem(RESEARCH_SESSION_KEY);
    } catch {
      // Continue when browser storage is unavailable.
    }
    setLocality(null);
    setSelectedLayers([]);
    requestVersion.current += 1;
    setBusy(false);
    setReport(null);
    setError("");
  };
  const update = (event) => {
    invalidate();
    setForm({
      ...form,
      ...(event.target.name === "address" || event.target.name === "postcode"
        ? { address_id: null }
        : {}),
      [event.target.name]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
      ...(event.target.name !== "confirmed" ? { confirmed: false } : {}),
    });
  };
  async function submit(event) {
    event.preventDefault();
    setLocality(null);
    setSelectedLayers([]);
    const version = ++requestVersion.current;
    setBusy(true);
    setError("");
    setReport(null);
    try {
      let submission = form;
      if (!form.address_id && /\d/.test(form.address)) {
        try {
          const { data: match } = await api.get("/research/addresses", {
            params: { q: form.address },
          });
          if (match.candidates?.length === 1) {
            const candidate = match.candidates[0];
            submission = {
              ...form,
              address: candidate.address,
              address_id: candidate.id,
              postcode: candidate.address.match(/QLD (4[0-9]{3})$/)?.[1] || form.postcode,
            };
            setForm(submission);
          }
        } catch {
          // Continue with suburb-level evidence when council matching is unavailable.
        }
      }
      const { data } = await api.post("/research/preview", {
        ...submission,
        address:
          fallbackGuide && submission.address.trim().length < 8
            ? `${fallbackGuide.name} QLD ${fallbackGuide.postcode}`
            : submission.address,
        price: Number(submission.price),
        postcode: submission.postcode || fallbackGuide?.postcode || null,
        bedrooms: submission.bedrooms ? Number(submission.bedrooms) : null,
      });
      if (version === requestVersion.current) {
        const nextReport = { ...data, prepared_at: new Date().toISOString() };
        setReport(nextReport);
        setLocality(data.locality || null);
        setSelectedLayers(
          (data.locality?.layers || [])
            .filter((layer) => layer.status !== "unavailable")
            .map((layer) => layer.id),
        );
      }
    } catch (err) {
      if (version !== requestVersion.current) return;
      setError(
        typeof err.response?.data?.detail === "string"
          ? err.response.data.detail
          : "We could not prepare your report. Please check your inputs and try again.",
      );
    } finally {
      if (version === requestVersion.current) setBusy(false);
    }
  }
  useEffect(() => {
    if (!report) return;
    try {
      window.sessionStorage.setItem(
        RESEARCH_SESSION_KEY,
        JSON.stringify({ inputs: form, report, locality, selectedLayers }),
      );
    } catch {
      // The report remains usable when browser storage is unavailable.
    }
  }, [form, locality, report, selectedLayers]);
  return (
    <div className={`research-workspace ${report ? "has-report" : ""}`}>
      {!report && (
        <ol className="research-progress" aria-label="Research steps">
          <li className="current">01 · Confirm the property</li>
          <li>02 · Review evidence</li>
          <li>03 · Explore your numbers</li>
        </ol>
      )}
      <div className="intro research-intro">
        <span className="eyebrow">
          Independent property research · Brisbane pilot
        </span>
        <h1>
          Your next property,
          <br />
          in perspective.
        </h1>
        <p>
          Start with what you know. See which facts still need checking, whoever
          is selling the property.
        </p>
      </div>
      <aside className="notice preview-notice">
        Brisbane pilot: matched reports combine council address, flood, zoning,
        bushfire, transport and park records with Queensland school catchments
        and RTA postcode rent benchmarks. Each finding shows its source and
        limits. This is not a valuation or a complete property risk assessment.
      </aside>
      <form
        id="property-details"
        onSubmit={submit}
        className="research-card property-form"
      >
        <span className="eyebrow">Your starting point</span>
        <h2>{report ? "Update property details" : "Research a property"}</h2>
        {params.has("address") && (
          <p className="notice">
            Details were prefilled from a link. Check and confirm them before
            continuing.
          </p>
        )}
        {linkedListing && (
          <aside className="selected-listing-prefill">
            <span className="eyebrow">Selected R2 package</span>
            <strong>{linkedListing.title}</strong>
            <p>{linkedListing.status}. Its advertised price and property type are prefilled below. The address remains private, so this report begins with suburb-level evidence.</p>
          </aside>
        )}
        <label>
          Property address or suburb
          <input
            required
            minLength={3}
            maxLength={300}
            name="address"
            value={form.address}
            onChange={update}
            autoComplete="street-address"
            placeholder="Street address or suburb, QLD"
          />
        </label>
        <AddressLookup
          key={form.address}
          query={form.address}
          autoSearch={params.has("address") && !form.address_id}
          fallbackLabel={fallbackGuide?.name || ""}
          onSelect={(candidate) => {
            invalidate();
            setForm({
              ...form,
              address: candidate.address,
              address_id: candidate.id,
              postcode: candidate.address.match(/QLD (4[0-9]{3})$/)?.[1] || "",
              confirmed: false,
            });
            setReport(null);
          }}
        />
        {form.address_id && (
          <p className="success-state" role="status">
            Council address matched. Complete the details below to load the
            property evidence.
          </p>
        )}
        {!form.address_id && fallbackGuide && (
          <aside className="suburb-fallback-card" aria-label="Suburb-level fallback active">
            <div>
              <span className="eyebrow">Suburb-level fallback active</span>
              <strong>{fallbackGuide.name}, {fallbackGuide.postcode}</strong>
              <p>Prop AI will use the available postcode rent benchmark and broader locality context. Flood, zoning, bushfire, school-catchment and property-distance claims remain parcel-specific and will not be inferred.</p>
            </div>
            <dl>
              <div><dt>Weekly rent benchmark</dt><dd>${fallbackGuide.weeklyRent.toLocaleString("en-AU")} / wk</dd></div>
              <div><dt>Local government</dt><dd>{fallbackGuide.lga}</dd></div>
            </dl>
            <Link to={`/suburbs/${fallbackGuide.slug}`}>Open full suburb dashboard →</Link>
          </aside>
        )}
        <div className="form-grid">
          <label>
            Purchase or asking price (AUD)
            <input
              required
              type="number"
              min="1"
              step="0.01"
              name="price"
              value={form.price}
              onChange={update}
            />
          </label>
          <label>
            Property type
            <select
              name="property_type"
              value={form.property_type}
              onChange={update}
            >
              <option value="house">House</option>
              <option value="townhouse">Townhouse</option>
              <option value="unit">Unit</option>
              <option value="land">Land</option>
              <option value="house_and_land">House and land</option>
            </select>
          </label>
        </div>
        <div className="form-grid">
          <label>
            Postcode for rent benchmark (optional)
            <input
              name="postcode"
              inputMode="numeric"
              pattern="4[0-9]{3}"
              maxLength={4}
              value={form.postcode || ""}
              onChange={update}
            />
          </label>
          <label>
            Bedrooms (optional)
            <input
              type="number"
              name="bedrooms"
              min="1"
              max="10"
              step="1"
              value={form.bedrooms || ""}
              onChange={update}
            />
          </label>
        </div>
        <label>
          My research goal
          <select name="goal" value={form.goal} onChange={update}>
            <option value="invest">Buying to invest</option>
            <option value="live_in">Buying to live in</option>
            <option value="own_review">Reviewing a property I own</option>
          </select>
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            name="confirmed"
            checked={form.confirmed}
            onChange={update}
            required
          />
          I have checked the {form.address_id ? "address" : "suburb"}, property type and price.
        </label>
        <button disabled={busy || !form.confirmed}>
          {busy ? "Preparing…" : "Prepare research preview"}
        </button>
        {busy && (
          <p role="status" className="loading-state">
            Checking available evidence and preparing your preview…
          </p>
        )}
        {error && (
          <p role="alert" className="error-state">
            {error} Your details are still here.
          </p>
        )}
      </form>
      {!report && <ReportAssistantPreview />}
      {report && (
        <section
          aria-label="Research report"
          aria-live="polite"
          className="report"
        >
          <div className="report-heading">
            <span className="eyebrow">Your property research</span>
            <h2 ref={reportHeading} tabIndex={-1}>
              {report.evidence.address}
            </h2>
            <a href="#property-details">Edit property details</a>
          </div>
          <ReportSummaryStrip metrics={presentedMetrics} />
          <nav className="report-nav" aria-label="Report sections">
            <a href="#overview">Overview</a>
            <a href="#locality">Locality</a>
            <a href="#evidence">Evidence</a>
            <a href="#ask-prop-ai">Ask Prop AI</a>
            <a href="#ownership">Ownership</a>
            <a href="#next-checks">Next checks</a>
            <a href="#save-report">Save report</a>
          </nav>
          <ReportCoverage report={report} metrics={presentedMetrics} />
          <p className="notice coverage-message">{report.coverage.message}</p>
          <ReportAssistant goal={form.goal} metrics={presentedMetrics} />
          <ResearchOverview
            report={report}
            goal={form.goal}
            locality={locality}
            selectedLayers={selectedLayers}
            metrics={presentedMetrics}
          />
          <LocalityPanel
            inputs={form}
            location={report.evidence.location}
            locality={locality}
            onChange={setLocality}
            selectedLayers={selectedLayers}
            onSelectedLayers={setSelectedLayers}
          />
          <p className="print-only print-meta">
            Prop AI research preview · prepared{" "}
            {new Date(report.prepared_at).toLocaleDateString("en-AU", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>

          <section className="goal-context" aria-label="Report focus">
            <h3>{goalPresentation[form.goal].heading}</h3>
            <p>{goalPresentation[form.goal].detail}</p>
          </section>
          <div className="section-heading" id="evidence">
            <span className="eyebrow">Sources, findings & gaps</span>
            <h2>The evidence behind your decision.</h2>
          </div>
          {sourcedMetrics.length === 0 && (
            <p className="notice">
              No evidence items were returned for this preview. Keep the
              property details and try again later; no property risks have been
              assessed.
            </p>
          )}
          <div className="metric-grid">
            {orderedMetrics(sourcedMetrics, form.goal).map(
              (metric, index, metrics) => {
                const groups = {
                  rent: "Rent & ownership",
                  purchase_costs: "Rent & ownership",
                  flood: "Property exposure",
                  planning: "Property exposure",
                  bushfire: "Property exposure",
                  schools: "Locality & daily life",
                  transport: "Locality & daily life",
                  neighbourhood: "Locality & daily life",
                  asking_price: "Your confirmed inputs",
                };
                const group = groups[metric.id] || "Other evidence";
                const previous = index
                  ? groups[metrics[index - 1].id] || "Other evidence"
                  : null;
                return (
                  <React.Fragment key={metric.id}>
                    {group !== previous && (
                      <h2 className="evidence-group-title">{group}</h2>
                    )}
                    <MetricCard metric={metric} />
                  </React.Fragment>
                );
              },
            )}
          </div>
          {unassessedMetrics.length > 0 && (
            <section className="evidence-gaps" id="evidence-gaps" aria-label="Unavailable assessments">
              <div className="section-heading">
                <span className="eyebrow">Coverage limitation</span>
                <h2>Could not be assessed at this location scope.</h2>
                <p>
                  Suburb-level insights are used where available. These checks
                  still need a matched property address and a complete response
                  from the cited public source. No parcel result has been inferred.
                </p>
              </div>
              <div className="evidence-gap-list">
                {orderedMetrics(unassessedMetrics, form.goal).map((metric) => (
                  <article key={metric.id}>
                    <div>
                      <h3>{metric.label}</h3>
                      <p>Not assessed · no result has been generated.</p>
                    </div>
                    <span>Property address required</span>
                  </article>
                ))}
              </div>
            </section>
          )}
          <EvidenceReading report={report} metrics={presentedMetrics} />
          <section className="ownership-cta" id="ownership">
            <div>
              <span className="eyebrow">Your costs and assumptions</span>
              <h2>What could ownership look like?</h2>
              <p>
                Open a focused workspace for purchase costs, cash flow and
                stress tests. Your property research stays here.
              </p>
            </div>
            <Link
              className="button-link"
              to="/ownership"
              onClick={() =>
                setOwnershipContext({
                  ...ownershipContext,
                  inputs: form,
                  report,
                  evidenceId: report.evidence_id,
                  explanationId: report.explanation?.explanation_id,
                  locality,
                  selectedLayers,
                })
              }
            >
              Build ownership scenario →
            </Link>
          </section>
          <div className="research-card">
            <h3 id="next-checks">Questions to check next</h3>
            <ul>
              {[
                ...goalPresentation[form.goal].questions,
                ...report.questions,
              ].map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
            <p>{report.disclaimer}</p>
          </div>
          <PartnerPanel goal={form.goal} propertyType={form.property_type} />
          <div className="research-card" id="save-report">
            <SaveReport
              inputs={form}
              evidenceId={report.evidence_id}
              explanationId={report.explanation?.explanation_id}
              finances={null}
              locality={locality}
            />
            <button
              className="secondary"
              type="button"
              onClick={() => window.print()}
            >
              Print or save as PDF
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
